import * as angular from 'angular';
import * as moment from 'moment';

import {
    ComponentDefinition,
    Identifier,
    GroupNameDto,
    AlarmList,
    IDisplayedColumn,
    IMappingDictionary,
    AlarmStoreSlice,
    IMenuItem,
    Dictionary
} from '../../../models';
import { IAlarmService, IExportToCsvService, IStoreService, IUserSettingsService } from '../../../services';
import { StoreElement, Store } from '../../../stateManagement';
import { AlarmRowViewModel } from '../../../models/alarms/AlarmRowViewModel';
import { FlatAlarmRowViewModel } from '@models/alarms/FlatAlarmRowViewModel';
import { alarmsTableColumnsKey } from '../../../common/constants';

const headers: IDisplayedColumn[] = [
    { isVisible: true, property: 'guid', type: 'guid', label: 'Guid' },
    { isVisible: true, property: 'identifierName', type: 'name', label: 'Name' },
    { isVisible: true, property: 'groupUniqueName', type: 'group', label: 'Group' },
    { isVisible: true, property: 'level', type: 'level', label: 'Level' },
    { isVisible: true, property: 'value', type: 'value', label: 'Value' },
    { isVisible: true, property: 'low', type: 'low', label: 'Low' },
    { isVisible: true, property: 'high', type: 'high', label: 'High' },
    { isVisible: true, property: 'mode', type: 'mode', label: 'Mode' },
    { isVisible: true, property: 'weight', type: 'weight', label: 'Weight' },
    { isVisible: true, property: 'probabilityOfFailure', type: 'pof', label: 'Pof' },
    { isVisible: true, property: 'consequenceOfFailure', type: 'cof', label: 'Cof' },
    { isVisible: true, property: 'comment', type: 'action', label: 'Action' },
    { isVisible: true, property: 'isDisabled', type: 'disabled', label: 'Disabled' }
];

const configurableColumns = ['Low', 'High', 'Mode', 'Delay', 'Weight', 'POF', 'COF', 'Action'];

class AlarmsStateController implements angular.IComponentController {
    alarmMenu: IMenuItem[];
    alarms: AlarmRowViewModel[];
    changedItems: AlarmRowViewModel[];
    columnsDic: Dictionary<boolean>;
    // fieldsToExport: IDisplayedColumn[];
    filteredAlarms: AlarmRowViewModel[];
    identifiers: Identifier[];
    isEditMode: boolean;
    isLoading: boolean;
    loadingMessage: string;
    selectedGroup: GroupNameDto;
    store: AlarmStoreSlice;
    tableFormController: angular.IFormController;

    private _unsubscribe: () => void;

    static $inject = ['storeService', 'exportToCsvService', 'alarmService', 'userSettingsService'];

    constructor(
        private _storeService: IStoreService,
        private _exportToCsvService: IExportToCsvService,
        private _alarmService: IAlarmService,
        private _userSettingsService: IUserSettingsService
    ) {}

    $onInit() {
        this.isLoading = true;
        this._unsubscribe = this._storeService.register(
            (store) => this.refreshStore(store),
            [StoreElement.Alarms, StoreElement.Groups, StoreElement.ValueViewModels, StoreElement.States]
        );
        this.isEditMode = false;
        this.initiazePropertyMapper(headers);
        const storedDic = this._userSettingsService.getTypedSettingValue<Dictionary<boolean>>(alarmsTableColumnsKey);
        const columnsDic: Dictionary<boolean> = {};
        const alarmMenu = configurableColumns.map((c) => {
            columnsDic[c] = storedDic ? storedDic[c] : true;
            const retval = {
                label: c,
                model: columnsDic[c],
                update: (value) => {
                    columnsDic[c] = !!value;
                    this._userSettingsService.setTypedSettingValue(alarmsTableColumnsKey, columnsDic);
                }
            };
            return retval;
        });
        this.alarmMenu = alarmMenu;
        this.columnsDic = columnsDic;
    }

    $onDestroy() {
        if (this._unsubscribe) {
            this._unsubscribe();
        }
    }

    private refreshStore(store: Store) {
        const alarmsDic = store.alarms.dictionary;
        const valueViewModelsDic = store.valueViewModels.dictionary;
        const statesDic = store.states.dictionary;
        const groupsDic = store.groups.dictionary;

        this.store = {
            alarmsDic,
            groupsDic,
            statesDic,
            valueViewModelsDic
        };
        this.alarms = this._alarmService.mapStoreToAlarmRowViewModels(this.store);
        this.isLoading = false;
    }

    save() {
        if (this.changedItems && this.changedItems.length > 0) {
            this.isLoading = true;
            const dic: AlarmList = {};
            this.changedItems.forEach((vm) => {
                if (!dic[vm.identifier.guid]) {
                    dic[vm.identifier.guid] = [];
                }
                dic[vm.identifier.guid].push(vm.alarm);
            });
            this._storeService
                .dispatchers()
                .alarms.updateMultiple(dic)
                .then((data) => {
                    this.tableFormController.$setPristine();
                    this.isEditMode = false;
                })
                .finally(() => (this.isLoading = false));
        }
    }

    cancel() {
        this.alarms = this._alarmService.mapStoreToAlarmRowViewModels(this.store);
        if (this.tableFormController) {
            this.tableFormController.$setPristine();
        }
        this.isEditMode = false;
    }

    edit() {
        this.isEditMode = true;
    }

    exportToCsv() {
        const groupName = this.selectedGroup ? `_${this.selectedGroup.name}` : '';
        const fileName = `Alarms${groupName}_${moment(new Date()).format('YYYY-MM-DD')}.csv`;
        const flatAlarmRowViewModels = this.filteredAlarms.map((a) => this.mapFloatAlarmRowViewModel(a));
        const data = this._exportToCsvService.getCsvContent(headers, flatAlarmRowViewModels, this._propertyMapper);
        this._exportToCsvService.getCsvFromData(data, fileName);
    }

    private _propertyMapper: IMappingDictionary<FlatAlarmRowViewModel>;

    private initiazePropertyMapper(headers: IDisplayedColumn[]) {
        this._propertyMapper = {};
        if (headers) {
            headers.forEach((m) => (this._propertyMapper[m.property] = (v) => v[m.property]));
        }
    }

    private mapFloatAlarmRowViewModel(alarmRvm: AlarmRowViewModel): FlatAlarmRowViewModel {
        return {
            guid: alarmRvm.alarm.guid,
            identifierName: alarmRvm.identifier.name,
            groupUniqueName: alarmRvm.identifier.groupUniqueName,
            level: alarmRvm.alarm.level.toString(),
            value: alarmRvm.alarm.value,
            mode: alarmRvm.alarm.mode.toString(),
            low: !alarmRvm.alarm.low && alarmRvm.alarm.low !== 0 && !alarmRvm.alarm.isManual ? '' : alarmRvm.alarm.low.toString(),
            high: !alarmRvm.alarm.high && alarmRvm.alarm.high !== 0 && !alarmRvm.alarm.isManual ? '' : alarmRvm.alarm.high.toString(),
            weight: !alarmRvm.alarm.weight && alarmRvm.alarm.weight !== 0 ? '' : alarmRvm.alarm.weight.toString(),
            probabilityOfFailure: alarmRvm.alarm.probabilityOfFailure,
            consequenceOfFailure: alarmRvm.alarm.consequenceOfFailure,
            comment: alarmRvm.alarm.comment,
            isDisabled: alarmRvm.alarm.isDisabled ? 'TRUE' : 'FALSE',
            isManual: !!alarmRvm.alarm.isManual
        };
    }
}

export const wtAlarmsState: ComponentDefinition = {
    componentId: 'wtAlarmsState',
    controller: AlarmsStateController,
    template: require('./alarmsState.component.html'),
    controllerAs: 'vm',
    bindings: {}
};
